import {
  CloseOutlined,
  HolderOutlined,
  LineOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { motion } from 'motion/react';
import {
  CSSProperties,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useSurfaceLift } from 'utils/surfaceStack';

interface CliToolPanelProps {
  title: string;
  icon: ReactNode;
  docked: boolean;
  visible: boolean;
  sideHost: HTMLElement | null;
  dockRect(): DOMRect | null;
  onFloat(): void;
  onDock(): void;
  onOverDock(over: boolean): void;
  onClose(): void;
  children: ReactNode;
}

const FLOAT_WIDTH = 380;
const FLOAT_HEIGHT = 420;
const EDGE = 8;
// Far enough that a click on the header is never read as a drag out of the dock.
const PULL_OUT = 6;

const SETTLE = {
  type: 'spring' as const,
  stiffness: 420,
  damping: 34,
  mass: 0.8,
};

interface Position {
  x: number;
  y: number;
}

const clampToViewport = ({ x, y }: Position): Position => ({
  x: Math.max(EDGE, Math.min(window.innerWidth - FLOAT_WIDTH - EDGE, x)),
  y: Math.max(EDGE, Math.min(window.innerHeight - FLOAT_HEIGHT - EDGE, y)),
});

const inside = (rect: DOMRect | null, x: number, y: number) =>
  !!rect &&
  x >= rect.left &&
  x <= rect.right &&
  y >= rect.top &&
  y <= rect.bottom;

// Docked or floating, it renders through one portal, so moving between them never unmounts the tool.
function CliToolPanel({
  title,
  icon,
  docked,
  visible,
  sideHost,
  dockRect,
  onFloat,
  onDock,
  onOverDock,
  onClose,
  children,
}: Readonly<CliToolPanelProps>) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const panelId = useId();
  const { lift, raise } = useSurfaceLift(`cli-tool:${panelId}`);

  // While the pointer is down the position is written straight to the element. Putting it in state
  // would re-render the dock -- and every open transcript with it -- on every pointer move.
  const live = useRef<Position>({ x: 0, y: 0 });
  const endDrag = useRef<(() => void) | null>(null);

  useEffect(() => () => endDrag.current?.(), []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest('button') || endDrag.current)
      return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;

    const from = { x: event.clientX, y: event.clientY };
    const origin = { x: rect.left, y: rect.top };
    let pulledOut = !docked;
    let overDock = false;

    live.current = origin;
    raise();

    // Listeners go on the window, not on the header: pulling the panel out of the dock reparents it
    // through the portal, and a node leaving the document loses any pointer capture it held.
    const move = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - from.x;
      const dy = moveEvent.clientY - from.y;

      if (!pulledOut) {
        if (Math.abs(dx) < PULL_OUT && Math.abs(dy) < PULL_OUT) return;
        // It leaves the dock exactly where it was sitting, then follows the pointer from there.
        pulledOut = true;
        live.current = clampToViewport(origin);
        setPosition(live.current);
        setDragging(true);
        onFloat();
        return;
      }

      live.current = clampToViewport({ x: origin.x + dx, y: origin.y + dy });
      const element = panelRef.current;
      if (element) {
        element.style.left = `${live.current.x}px`;
        element.style.top = `${live.current.y}px`;
      }

      // The pointer decides, not the panel's own box: a 380px panel overlaps the dock long
      // before you mean to put it back, and the grip is what you are aiming with.
      const over = inside(dockRect(), moveEvent.clientX, moveEvent.clientY);
      if (over !== overDock) {
        overDock = over;
        onOverDock(over);
      }
    };

    const finish = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      endDrag.current = null;
      setDragging(false);
      setPosition(live.current);
      if (overDock) {
        onOverDock(false);
        onDock();
      }
    };

    endDrag.current = finish;
    setDragging(true);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
  };

  if (docked && !sideHost) return null;

  const style = {
    '--surface-lift': lift,
    ...(docked ? null : { left: position.x, top: position.y }),
  } as CSSProperties;

  const panel = (
    <motion.div
      ref={panelRef}
      className={`CliToolPanel CliToolPanel--${docked ? 'docked' : 'floating'}${dragging ? ' is-dragging' : ''}`}
      style={style}
      hidden={!visible}
      role="region"
      aria-label={title}
      initial={{ opacity: 0, scale: 0.97, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={SETTLE}
      onPointerDownCapture={raise}
    >
      <div className="CliToolPanel__header" onPointerDown={onPointerDown}>
        <HolderOutlined className="CliToolPanel__grip" />
        <span className="CliToolPanel__title">
          {icon}
          {title}
        </span>
        <button
          type="button"
          className="CliToolPanel__close"
          onClick={onClose}
          aria-label={`Close ${title}`}
        >
          <CloseOutlined />
        </button>
      </div>

      <div className="CliToolPanel__body">{children}</div>

      {/* Bottom right, where a pane's actions sit everywhere else in the app. */}
      <div className="CliToolPanel__actions">
        {docked ? (
          <button
            type="button"
            className="CliToolPanel__action"
            onClick={onClose}
          >
            <LineOutlined /> Minimize
          </button>
        ) : (
          <button
            type="button"
            className="CliToolPanel__action"
            onClick={onDock}
          >
            <LoginOutlined /> Back to terminal
          </button>
        )}
      </div>
    </motion.div>
  );

  return createPortal(panel, docked && sideHost ? sideHost : document.body);
}

export default CliToolPanel;
